/**
 * Design tokens.
 *
 * Single source of truth for color, type, motion, and spacing across the HUD,
 * overlays, and any new mission-control surface. Tokens are referenced by name
 * (never inlined) so a theme swap or a CSS variable bridge can land without
 * touching component code.
 */

export type ColorRole =
  | 'background'
  | 'surface'
  | 'text'
  | 'border'
  | 'semantic'
  | 'globe';

export type MotionDuration = 'instant' | 'fast' | 'medium' | 'slow' | 'cinematic';

export const tokens = {
  color: {
    background: {
      base: '#02060c',
      grid: '#0a1018',
      vignette: 'rgba(0, 4, 10, 0.85)',
    },
    surface: {
      glass: 'rgba(8, 16, 28, 0.55)',
      innerGlow: 'rgba(76, 215, 255, 0.06)',
      topHighlight: 'rgba(255, 255, 255, 0.04)',
    },
    text: {
      primary: '#e6f4ff',
      muted: '#7a93a8',
      faint: '#3e5366',
      accent: '#6cf0ff',
    },
    border: {
      neutral: 'rgba(108, 240, 255, 0.18)',
      info: 'rgba(108, 240, 255, 0.45)',
      warn: 'rgba(255, 184, 76, 0.55)',
      critical: 'rgba(255, 76, 96, 0.65)',
    },
    semantic: {
      good: '#5fffa1',
      warn: '#ffb84c',
      critical: '#ff4c60',
      info: '#6cf0ff',
    },
    /**
     * Globe layer palette — one source of truth for every map color, shared
     * with the HUD. Markers (ixp/pop/link) carry the live network; the
     * substrate (coastline/cable/cloud/facility) recedes; surface/rim/lights
     * drive the Earth shader, atmosphere halo, and scene lighting.
     */
    globe: {
      coastline: '#3f7fb0',
      cable: '#ffe83a',
      cloudRegion: '#ff8c1a',
      facility: '#ff6fb5',
      ixp: '#39ff14',
      pop: '#39ff14',
      link: '#39ff14',
      incident: '#ff5a5a',
      surface: '#0b1a2a',
      rim: '#4ad7ff',
      keyLight: '#bfe7ff',
      fillLight: '#4a7ab8',
    },
  },
  type: {
    family: {
      sans: '"InterVariable", "Inter", system-ui, sans-serif',
      mono: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
    },
    size: {
      xs: '10px',
      sm: '12px',
      md: '14px',
      lg: '16px',
      xl: '20px',
      display: '32px',
    },
    weight: {
      regular: 400,
      medium: 500,
      semibold: 600,
    },
  },
  motion: {
    instant: 0,
    fast: 120,
    medium: 240,
    slow: 480,
    cinematic: 1200,
  } satisfies Record<MotionDuration, number>,
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 40,
  },
  z: {
    scene: 0,
    hud: 10,
    overlay: 20,
    dialog: 30,
    toast: 40,
  },
} as const;

export type Tokens = typeof tokens;
